/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { HouseRounded, AddLocation } from "@material-ui/icons";
import ReactMapGL, {
    Marker,
    Popup,
    GeolocateControl,
    NavigationControl,
    MapLoadEvent,
    MapRef,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "react-map-gl-geocoder/dist/mapbox-gl-geocoder.css";
import Geocoder from "react-map-gl-geocoder";
import { makeStyles, createStyles } from "@material-ui/styles";
import { serverConfig } from "../../../utils/config";
import { PlaceInfo } from "./components/PlaceInfo";
import { SearchMarker } from "./components/SearchMarker";
import mapboxgl, { LngLatBounds } from "mapbox-gl";
import { useTranslation } from "react-i18next";


const useStyles = makeStyles((theme) =>
    createStyles({
        container: {
            display: "flex",
            alignItems: "center",
            zIndex: 1,
            boxShadow: "0 30px 40px 0 rgb(16 36 94 / 20%)",
        },
        map: {
            // marginTop: 10
        },
        geocoder: {
            // margin: 10
        },
        pin: {
            color: "#FFFCF0",
            backgroundColor: "#EA7606",
            padding: "2px",
            border: "solid #FFFCF0 1px",
            borderRadius: "10px",
            transform: "scale3d(1.5, 1.5, 1)",
            zIndex: 1,
        },
        circle: {
            color: "#890608",
            transition: "transform 0.15s ease-in-out",
            "&:hover": {
                transform: "scale3d(1.5, 1.5, 1)",
                color: "#EA7606",
            },
            zIndex: 1,
        },
        popup: {
            zIndex: 3,
        },
    })
);

const mapboxApiKey = serverConfig.MapBoxApikey;

export interface IMapProps {
    height?: string | number;
    width?: string | number;
    alignStart?: boolean;
    setLocation?: boolean;
    updateLocation?: boolean;
    autoGeolocate?: boolean;
    setPin?: boolean;
    startLongitude?: number;
    startLatitude?: number;
    oldLongitude?: number;
    oldLatitude?: number;
    startZoom?: number;
    handleChange?: any;
    setMarkers?: boolean;
    markers?: any;
    boundaries?: any;
    onBoundariesChange?: any;
    isSearchingOnMap?: boolean;
    handleNext?: () => void;
}

interface MyGeoError {
  code: number;
  message: string;
}


export function MapView(props: IMapProps) {
    const [viewport, setViewport] = React.useState({
        longitude: props.startLongitude ?? 19.13697660832946,
        latitude: props.startLatitude ?? -1.2138498098141102,
        zoom: props.startZoom ?? 2,
    });

    const [geolocationAvailable, setGeolocationAvailable] = React.useState(false);
    const [geolocationError, setGeolocationError] = React.useState<string | null>(null);

    const [pins, setPins] = React.useState([]);

    const [currentPlaceId, setCurrentPlaceId] = React.useState(null);

    const [isOnhover, setIsOnHover] = React.useState(null);

    const [showPins, setShowPins] = React.useState(false);

    const [showPrice, setShowPrice] = React.useState(false);

    const [boundary, setBoundary] = React.useState<LngLatBounds>();

    const handleMarkerClick = (id: any) => {
        setCurrentPlaceId(id);
    };

    const handleHoverOn = (id: any) => {
        setIsOnHover(id);
        setShowPrice(true);
    };

    const handleHoverOff = () => {
        setIsOnHover(null);
        setShowPrice(false);
    };

    const handleViewportChange = React.useCallback((newViewport) => {
         //console.log("[Viewport] Changed to:", newViewport);
        setViewport(newViewport);
        if (props.setLocation && props.handleChange) {
            props.handleChange(newViewport.longitude, newViewport.latitude);
        }
    }, []);

    const handleGeocoderViewportChange = React.useCallback((newViewport) => {
        const geocoderDefaultOverrides = { transitionDuration: 1000 };
        return handleViewportChange({
            ...newViewport,
            ...geocoderDefaultOverrides,
        });
    }, []);

    const { t } = useTranslation();

    

    React.useEffect(() => {
        if (props.setMarkers) {
            if (props.markers.length > 0) {
                setShowPins(true);
                setPins(props.markers);
            }
        }
    }, [props.markers]);

    React.useEffect(() => {
        const bounds = mapInstance.current?.getBounds();
        if (bounds) { // Ensure bounds is not null
            setBoundary(bounds);
        }
    }, [viewport]);

    React.useEffect(() => {
        if (boundary && props.isSearchingOnMap) {
            props.onBoundariesChange({
                East: boundary.getEast(),
                West: boundary.getWest(),
                North: boundary.getNorth(),
                South: boundary.getSouth(),
                Center: boundary.getCenter(),
                Zoom: mapInstance.current?.getZoom(),
            });
        }
    }, [boundary]);

    
    const handleRecenter = (
        longitude: number,
        latitude: number,
        zoom: number,
        transitionDuration: number
    ) => {
        const center = {
            longitude,
            latitude,
            zoom,
            transitionDuration,
        };
        handleViewportChange(center);
    };

    React.useEffect(() => {
        if (
            props.startLongitude &&
            props.startLatitude &&
            props.startZoom &&
            props.setMarkers === true
        ) {
            handleRecenter(
                props.startLongitude,
                props.startLatitude,
                props.startZoom,
                500
            );
        }
    }, [props.setMarkers, props.startLongitude, props.startLatitude]);

    const mapRef = React.useRef<MapRef>(null);
    const mapInstance = React.useRef<mapboxgl.Map | null>(null);

    React.useEffect(() => {
        if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        console.warn('Geolocation requires HTTPS in production');
        }
    }, []);

    // Check geolocation support and permissions
    React.useEffect(() => {
        //console.log("[Geolocation] Checking browser support...");
        if (!("geolocation" in navigator)) {
            //console.error("[Geolocation] NOT SUPPORTED by browser");
            setGeolocationError("Geolocation is not supported by your browser");
            return;
        }
        //console.log("[Geolocation] Browser supports geolocation");

        setGeolocationAvailable(true);

        if (navigator.permissions?.query) {
            //console.log("[Geolocation] Checking permission status...");
            navigator.permissions.query({ name: 'geolocation' }).then(permissionStatus => {
                //console.log(`[Geolocation] Initial permission state: ${permissionStatus.state}`);
                if (permissionStatus.state === 'denied') {
                    //console.warn("[Geolocation] Permission DENIED by user");
                    setGeolocationError("Location access was denied. Please enable permissions.");
                }
                permissionStatus.onchange = () => {
                    //console.log(`[Geolocation] Permission changed to: ${permissionStatus.state}`);
                    setGeolocationError(
                        permissionStatus.state === 'denied' 
                            ? "Location access denied" 
                            : null
                    );
                };
            }).catch(() => {
                //console.error("[Geolocation] Permission query failed:", Error);
                // Permission API not supported, continue anyway
            });
        }
    }, []);


    // Create a proper GeolocationPositionError without modifying read-only properties
    /*const createGeolocationError = (code: 1 | 2 | 3, message: string): GeolocationPositionError => {
    // Use Object.defineProperty to create immutable properties
    const error = new Error(message) as Partial<GeolocationPositionError>;
    
    Object.defineProperties(error, {
        code: {
        value: code,
        enumerable: true,
        writable: false,
        configurable: false
        },
        PERMISSION_DENIED: {
        value: 1,
        enumerable: true,
        writable: false,
        configurable: false
        },
        POSITION_UNAVAILABLE: {
        value: 2,
        enumerable: true,
        writable: false,
        configurable: false
        },
        TIMEOUT: {
        value: 3,
        enumerable: true,
        writable: false,
        configurable: false
        }
    });

    return error as GeolocationPositionError;
    }; */

    interface SafeGeolocateProps {
        style?: React.CSSProperties;
        positionOptions?: PositionOptions;
        trackUserLocation?: boolean;
        auto?: boolean;
        onGeolocate?: (position: GeolocationPosition) => void;
        onError?: (error: GeolocationPositionError) => void;
    }

    function SafeGeolocate({
    style,
    positionOptions,
    trackUserLocation,
    auto,
    onGeolocate,
    onError
    }: SafeGeolocateProps) {
    const [error, setError] = React.useState<GeolocationPositionError | null>(null);

    React.useEffect(() => {
        if (!auto) return;

        const geo = navigator.geolocation;
        if (!geo || typeof geo.watchPosition !== 'function') {
        const err = {
            code: 0,
            message: 'Geolocation not fully supported',
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3,
        } as GeolocationPositionError;
        setError(err);
        onError?.(err);
        return;
        }

        const watchId = geo.watchPosition(
        (position) => {
            onGeolocate?.(position);
        },
        (err) => {
            setError(err);
            onError?.(err);
        },
        positionOptions
        );

        return () => {
        geo.clearWatch(watchId);
        };
    }, [auto, onGeolocate, onError, positionOptions]);

    return (
        <>
            {/*<GeolocateControl
                style={style}
                positionOptions={positionOptions}
                trackUserLocation={trackUserLocation}
                auto={auto}
                onGeolocate={(position: GeolocationPosition) => {
                    //console.log('[Geolocation] User position:', position);
                }}
            /> */}

            {typeof navigator !== 'undefined' && navigator.geolocation ? (
                <GeolocateControl
                    style={style}
                    positionOptions={positionOptions}
                    trackUserLocation={trackUserLocation}
                    auto={false} // safer to control manually
                    onGeolocate={(position: GeolocationPosition) => {
                    //console.log('[Geolocation] User position:', position);
                }}
                />
            ) : (
                <p>Geolocation is not available.</p>
            )}


            {error && (
                <div style={{ color: 'red', fontSize: 12 }}>
                Geolocation Error: {error.message}
                </div>
            )}
        </>
    );
    }


    const handleLoad = (event: MapLoadEvent) => {
        //console.log("[Map] Map loaded successfully");
        const { target } = event;
        mapInstance.current = target as unknown as mapboxgl.Map; // Fix 2: Type assertion
        
        const bounds = mapInstance.current?.getBounds(); // Fix 3: Optional chaining
        //console.log("[Map] Initial bounds:", bounds);
        if (bounds) {
            setBoundary(bounds);
        }
    };


    //const map = React.useRef<mapboxgl.Map>();

    const handleCloseButton = () => {
        setCurrentPlaceId(null);
    };
    const classes = useStyles();

    return (
        <div
            className={classes.container}
            style={{ alignItems: props.alignStart ? "start" : "center" }}
        >
            {geolocationError && (
                <div className="geolocation-error-banner">
                    {geolocationError}
                </div>
            )}
            <ReactMapGL
                ref={mapRef}
                mapboxApiAccessToken={mapboxApiKey}
                mapStyle="mapbox://styles/mapbox/streets-v11"//"mapbox://styles/santatra024/ckt8erdte4vy317mw1v2yyzu7"//"mapbox://styles/christepo/cm7wey59q014v01sbf5ca1j9o"  
                {...viewport}
                height={props.height ?? 500}
                width={props.width ?? "100%"}
                onViewportChange={handleViewportChange}
                className={classes.map}
                maxZoom={props.setPin || props.setMarkers ? 14 : 20}
                minZoom={props.setPin ? 4.5 : 0}
                onLoad={handleLoad}
            >
                <Geocoder
                    mapRef={mapRef}
                    mapboxApiAccessToken={mapboxApiKey}
                    onViewportChange={handleGeocoderViewportChange}
                    position="top-left"
                    collapsed={true}
                    clearAndBlurOnEsc={true}
                    reverseGeocode={true}
                    marker={false}
                />
                {props.setLocation && (
                    <Marker
                        longitude={viewport.longitude}
                        latitude={viewport.latitude}
                    >
                        <div
                            className={classes.circle}
                            onClick={props.handleNext}
                        >
                            <div>
                                <label> {t("label.marquer-ici")} </label>
                            </div>
                            <div>
                                <AddLocation />
                            </div>
                        </div>
                    </Marker>
                )}
                {props.updateLocation && (
                    <Marker
                        longitude={props.oldLongitude ?? 0}
                        latitude={props.oldLatitude ?? 0}
                    >
                        <div
                            className={classes.circle}
                            onClick={() =>
                                handleRecenter(
                                    props.oldLongitude ?? 0,
                                    props.oldLatitude ?? 0,
                                    props.startZoom ?? 13,
                                    500
                                )
                            }
                        >
                            <HouseRounded fontSize={"large"} />
                        </div>
                    </Marker>
                )}
                {props.setPin && (
                    <Marker
                        longitude={props.startLongitude ?? 0}
                        latitude={props.startLatitude ?? 0}
                    >
                        <div
                            className={classes.circle}
                            onClick={() =>
                                handleRecenter(
                                    props.startLongitude ?? 0,
                                    props.startLatitude ?? 0,
                                    props.startZoom ?? 13,
                                    500
                                )
                            }
                        >
                            <HouseRounded fontSize={"large"} />
                        </div>
                    </Marker>
                )}
                {props.setMarkers &&
                    showPins &&
                    pins.map((p: any) => (
                        <div>
                            <SearchMarker
                                p={p}
                                handleMarkerClick={handleMarkerClick}
                                handleHoverOn={handleHoverOn}
                                handleHoverOff={handleHoverOff}
                                showPrice={showPrice}
                                isOnhover={isOnhover}
                                viewport={viewport}
                            />
                            {p._id === currentPlaceId && (
                                <Popup
                                    offsetLeft={15}
                                    offsetTop={10}
                                    longitude={
                                        p.propertyData[0].location.longitude
                                    }
                                    latitude={
                                        p.propertyData[0].location.latitude
                                    }
                                    anchor="bottom"
                                    closeButton={false}
                                    onClose={() => {
                                        setCurrentPlaceId(null);
                                    }}
                                    className={classes.popup}
                                >
                                    <PlaceInfo
                                        datas={p}
                                        handleCloseButton={handleCloseButton}
                                    />
                                </Popup>
                            )}
                        </div>
                    ))}

                <SafeGeolocate
                    style={{ right: 10, top: 10 }}
                    positionOptions={{
                        enableHighAccuracy: true,
                        maximumAge: 0,
                        timeout: 20000
                    }}
                    trackUserLocation={true}
                    auto={props.autoGeolocate}
                    onGeolocate={(pos) => {/* optional callback */}}
                    onError={(err) => {
                        setGeolocationError("Location not available or unsupported");
                        console.error(err);
                    }}
                />

                <NavigationControl style={{ right: 10, bottom: 25 }} />
            </ReactMapGL>
        </div>
    );
}
